import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Eye, LayoutGrid, List, Monitor, MoreVertical, Pencil, Plus, Search, Smartphone, Sparkles, Tablet, Trash2, X } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import { useBuilder } from './hooks/useBuilder'
import { DEVICE_PREVIEW_WIDTHS } from './editable/responsiveVisibility'
import { BuilderBlockPreview, BuilderBlockPreviewFrame } from './BuilderBlockPreview'
import { BuilderDefDeleteModal } from './BuilderDefDeleteModal'
import { BuilderDefMetaModal } from './BuilderDefMetaModal'
import { BLOCK_TARGET_EVENT, buildCreateContext, buildCreateFromContext, buildDefEditContext } from './chat/blockContext'
import { fetchDefUsageCounts } from './chat/blockUsage'
import { setPendingPlacement } from './blockPlacement'
import { notify } from '../../notices'
import { useScrimDismiss } from '../shared/hooks/useScrimDismiss'
import { useEscapeClose } from '../shared/hooks/useEscapeClose'
import { largeurUtile } from '../shell/cornerDock';

// Sentinelle « aucun filtre de catégorie ». Distincte du libellé affiché : une
// catégorie qui s'appellerait « Toutes » casserait la comparaison avec
// `block.category`, et traduire le libellé casserait l'état.
const ALL = '__all__'
const ALL_LABEL = 'Toutes'

// Soft category the dashboard host owns (its grid + widgets). A block is placed
// in a host by tagging its `category`, not by a hard type — the host declares
// which categories its catalogue shows.
export const DASHBOARD_CATEGORY = 'Dashboard'

// Catégorie technique : des blocs posés par un geste dédié, jamais choisis dans
// la liste « Ajouter un bloc ». Aujourd'hui le seul porteur `SharedBlock`, inséré
// par « Ajouter un bloc existant » : le proposer aussi au catalogue général
// donnerait un bloc vide sans cible.
//
// Clé ASCII et comparée telle quelle : une catégorie accentuée mal orthographiée
// dans une déf (« Systeme ») ferait silencieusement retomber le bloc dans le
// catalogue général. Elle n'est jamais affichée — ces blocs n'y figurent pas.
export const SYSTEM_CATEGORY = 'system'

/**
 * Le cloisonnement par site, en une fonction : ce bloc a-t-il sa place au
 * catalogue d'une page rattachée à `siteId` (ou à aucun site) ?
 *
 * Partagée entre le filtrage de la liste et le message qui suit un changement de
 * site — dire « ce bloc n'est plus proposé ici » suppose la MÊME règle que celle
 * qui vient de le faire disparaître, pas une seconde écrite à côté.
 *
 * La question se pose sur `siteIds`, la LISTE des rattachements, jamais sur
 * l'étiquette `siteId` : une déf mutualisée entre marques (les blocs de landing
 * appartiennent à cinq sites) n'a qu'une étiquette, et comparer dessus vidait
 * le catalogue des quatre autres — mesuré sur « Trained People », dont les
 * treize défs sont toutes partagées.
 */
const siteIdsOf = (block) => (
  Array.isArray(block.siteIds) && block.siteIds.length
    ? block.siteIds
    : (block.siteId ? [block.siteId] : [])
)

const belongsToSite = (block, siteId) => {
  const ids = siteIdsOf(block)
  return siteId
    ? (ids.includes(Number(siteId)) || block.category === DASHBOARD_CATEGORY)
    : ids.length === 0
}

/**
 * Which blocks a host's catalogue offers, gated on the block's single `category`:
 * a dashboard host offers the dashboard category (its grid + widgets); a page
 * host offers everything else.
 */
const hostAcceptsCategory = (host, category) =>
  host
    ? category === DASHBOARD_CATEGORY
    : category !== DASHBOARD_CATEGORY

// « Non utilisé » = zéro contenu d'après le serveur. Un type ABSENT de la carte
// (déf créée après sa lecture) n'est pas inutilisé : son compte est inconnu.
const isUnused = (counts, block) => counts?.[block.type] === 0

// Les descriptions de blocs sont du Markdown mêlé de HTML (<code>, <em>…).
// Pour l'aperçu compact des lignes, on réduit le tout à du texte brut.
const stripFormatting = (str = '') =>
  str
    .replace(/<[^>]+>/g, '')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

/**
 * Le catalogue « Ajouter un bloc ».
 *
 * Trois règles portées par cet écran :
 *
 *  1. CLOISONNEMENT PAR SITE — une page rattachée à un site ne se compose
 *     qu'avec les défs de CE site. Le rattachement est une relation de groupe
 *     exposée par `orbit_site` sous `siteId` (cf. DecoupledOutputSubscriber) ;
 *     le back-office ne filtre rien côté requête (aucun site ne revendique son
 *     host), c'est donc ici que ça se joue. Et le cloisonnement vaut DANS LES
 *     DEUX SENS : une page SANS site — administration, démo, tableaux de bord —
 *     ne voit que la bibliothèque partagée (`siteId: null`), jamais les blocs
 *     d'un site.
 *  2. DEUX AFFICHAGES — « Liste » (dense, pour retrouver un bloc qu'on connaît)
 *     et « Rendu » (chaque déf réellement montée avec ses données par défaut,
 *     pour reconnaître un bloc qu'on ne connaît pas).
 *  3. SUR-POP-IN D'APERÇU — depuis les deux affichages, un bloc s'inspecte en
 *     grand (rendu grandeur nature + description) avant d'être ajouté.
 */
/**
 * Hand the assistant a context and get out of the way: the catalogue closes, the
 * chat panel opens with the context pinned and the composer focused. Nothing is
 * sent — the editor writes the request.
 *
 * `place` is remembered for the whole turn (see `blockPlacement`) so a block the
 * agent ends up creating lands where the editor clicked "+", not at the end of
 * the page.
 */
const askAssistant = (context, place, onClose, expectedType = null, spot = null) => {
  setPendingPlacement(place ?? null, { type: expectedType, spot })
  onClose()
  window.dispatchEvent(new CustomEvent(BLOCK_TARGET_EVENT, { detail: context }))
}

/**
 * @param {object} props
 * @param {object} [props.placementSpot]
 *   Où l'hôte dessinera le carré d'attente pendant le tour. Par défaut la place
 *   d'insertion de la page ; une colonne ou une case de grille passe la sienne,
 *   qu'elle seule sait interpréter.
 */
export const BuilderBlockCatalog = ({ position, onClose, onSelect, accept, onPlace, placementSpot }) => {
  const scrim = useScrimDismiss(onClose)
  const { registry, setRegistry, addBlock, host, page } = useBuilder()
  const [search, setSearch] = useState('')
  const [activeCategory, setActiveCategory] = useState(ALL)
  // « Rendu » à l'ouverture : on choisit un bloc en le voyant. La « Liste »
  // reste à un clic pour qui cherche un bloc dont il connaît déjà le nom.
  const [view, setView] = useState('render')
  const [device, setDevice] = useState('desktop')
  const [previewBlock, setPreviewBlock] = useState(null)
  // Les deux pop-ins d'administration d'une DÉFINITION : ses informations de
  // catalogue, et sa suppression. Une seule est ouverte à la fois — elles
  // partent du même menu de ligne.
  const [metaBlock, setMetaBlock] = useState(null)
  const [deleteBlock, setDeleteBlock] = useState(null)
  // Combien de contenus posent chaque déf. NULL tant que la réponse n'est pas là,
  // ou si elle a été refusée : le filtre « Non utilisés » n'apparaît alors pas,
  // plutôt que de classer tout le catalogue parmi les inutilisés.
  const [usageCounts, setUsageCounts] = useState(null)
  const [onlyUnused, setOnlyUnused] = useState(false)
  const titleId = useId()
  const unusedHintId = useId()

  // Une requête pour toute la bibliothèque, à l'ouverture — pas une par ligne.
  useEffect(() => {
    const controller = new AbortController()
    fetchDefUsageCounts(controller.signal).then(counts => {
      if (!controller.signal.aborted) setUsageCounts(counts)
    })
    return () => controller.abort()
  }, [])

  const siteId = page?.siteId ?? null
  const siteName = page?.siteName ?? ''

  // Which blocks this catalogue offers. `accept(def)` lets the caller decide per
  // CONTEXT (a dashboard top-level offers generic blocks + grids; a dashboard
  // grid CELL offers only widgets) — otherwise fall back to the host-category
  // default (a dashboard host → its category; a page host → everything else).
  // Le cloisonnement par site s'applique PAR-DESSUS, jamais à la place : les
  // deux répondent à des questions différentes (« quoi ici ? » / « à qui ? »).
  //
  // Une exception, et une seule : les widgets de tableau de bord. Ce sont des
  // outils de back-office, pas du contenu de site, et ils vivent en
  // bibliothèque partagée (`siteId: null`) ; les cloisonner viderait la
  // cellule de grille le jour où un tableau de bord serait rattaché à un site.
  //
  // Hors contexte de site, la règle ne s'annule pas, elle se retourne : seule la
  // bibliothèque partagée est proposée. Une déf créée depuis le catalogue d'un
  // site lui est rattachée (cf. /api/builder/def/site) ; servir « tout » au
  // back-office y déversait les blocs de chaque client, dont aucun n'a sa place
  // sur une page qui n'est pas la leur.
  const eligible = useMemo(() => {
    const accepted = registry.filter(accept ?? (b => hostAcceptsCategory(host, b.category)))
    return accepted.filter(b => belongsToSite(b, siteId))
  }, [registry, host, accept, siteId])

  // Compté sur les blocs ÉLIGIBLES (site, hôte), avant catégorie et recherche :
  // le chiffre du filtre ne bouge pas à chaque frappe.
  const unusedCount = useMemo(
    () => (usageCounts ? eligible.filter(b => isUnused(usageCounts, b)).length : 0),
    [eligible, usageCounts],
  )

  // « Non utilisés » restreint AVANT les catégories : une catégorie dont chaque
  // bloc sert quelque part n'a plus de pastille à offrir.
  const scoped = useMemo(
    () => (onlyUnused && usageCounts ? eligible.filter(b => isUnused(usageCounts, b)) : eligible),
    [eligible, onlyUnused, usageCounts],
  )

  // Liste ordonnée des catégories présentes dans le registry éligible
  const categories = useMemo(() => {
    const seen = new Set()
    scoped.forEach(b => { if (b.category) seen.add(b.category) })
    return [ALL, ...seen]
  }, [scoped])

  // Une catégorie sélectionnée puis vidée (recherche, changement de site)
  // laisserait un écran vide sans explication : on retombe sur « Toutes ».
  useEffect(() => {
    if (activeCategory !== ALL && !categories.includes(activeCategory)) setActiveCategory(ALL)
  }, [categories, activeCategory])

  const filtered = useMemo(() => {
    let list = scoped

    if (activeCategory !== ALL) {
      list = list.filter(b => b.category === activeCategory)
    }

    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter(b =>
        b.name?.toLowerCase().includes(q) ||
        b.description?.toLowerCase().includes(q) ||
        b.category?.toLowerCase().includes(q)
      )
    }

    return list
  }, [scoped, activeCategory, search])

  // Grouper par catégorie pour l'affichage
  const grouped = useMemo(() => {
    return filtered.reduce((acc, block) => {
      const cat = block.category || 'Général'
      if (!acc[cat]) acc[cat] = []
      acc[cat].push(block)
      return acc
    }, {})
  }, [filtered])

  // Les catégories déjà en usage, proposées en autocomplétion dans la pop-in
  // d'informations.
  const knownCategories = useMemo(
    () => [...new Set(registry.map(b => b.category).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'fr')),
    [registry],
  )

  // Rang de chaque bloc dans la galerie, tous groupes confondus : l'affichage
  // « Rendu » s'en sert pour étaler les montages (cf. BuilderBlockPreview).
  const renderOrder = useMemo(
    () => new Map(filtered.map((b, i) => [b.type, i])),
    [filtered]
  )

  const handleSelect = useCallback((blockDef) => {
    // `onSelect` override (e.g. a GridLayout adding into a cell); otherwise the
    // default page-level add.
    if (onSelect) onSelect(blockDef)
    else addBlock(blockDef, position)
    onClose()
  }, [onSelect, addBlock, position, onClose])

  // Where a block created during this turn must land — the same slot the manual
  // "Ajouter" uses, so the assistant path and the catalogue path agree.
  const place = useCallback(
    (blockDef) => (onPlace ? onPlace(blockDef) : (onSelect ? onSelect(blockDef) : addBlock(blockDef, position))),
    [onPlace, onSelect, addBlock, position],
  )

  // Créer un bloc part d'une page BLANCHE : rien de l'écran courant ne voyage,
  // seulement le site auquel la définition sera rattachée (cf. buildCreateContext).
  // La place cliquée voyage avec le placeur, pour que le canevas y dessine le
  // carré d'attente pendant tout le tour : sans elle, l'éditeur ne verrait plus
  // rien entre son clic et la réponse.
  const spot = useMemo(
    () => placementSpot ?? { kind: 'page', at: position },
    [placementSpot, position],
  )

  const handleCreate = useCallback(
    () => askAssistant(buildCreateContext({ page }), place, onClose, null, spot),
    [page, place, onClose, spot],
  )

  // Editing a DEFINITION changes every instance of the type, everywhere. But the
  // editor got here by clicking "+" at a spot: they want THIS block there, tuned
  // first. So the placer is armed on that very type — it lands once the turn has
  // actually changed the definition (see BuilderCanvas), not on a mere question.
  const handleAiEdit = useCallback(
    (blockDef) => askAssistant(
      buildDefEditContext({ def: blockDef, page, host }), place, onClose, blockDef.type, spot,
    ),
    [page, host, place, onClose, spot],
  )

  const handleAiDerive = useCallback(
    (blockDef) => askAssistant(buildCreateFromContext({ def: blockDef, page, host }), place, onClose, null, spot),
    [page, host, place, onClose, spot],
  )

  // Métadonnées enregistrées : l'entrée du registry est remplacée sur place
  // plutôt que la collection rechargée — l'écriture est déjà confirmée par
  // Drupal, et un rechargement démonterait la galerie de rendus sous les yeux de
  // l'éditeur.
  //
  // La SIGNATURE est recalculée depuis le YAML que Drupal vient de stocker, avec
  // la même recette que `normalizeRegistryItem` (nom + définition). Sans cela le
  // prochain rechargement du registry verrait une signature différente,
  // conclurait à une définition modifiée, et remonterait toutes les instances du
  // type — pour un simple renommage.
  const handleMetaSaved = useCallback((blockDef, saved) => {
    setRegistry(prev => prev.map(entry => (entry.type !== blockDef.type ? entry : {
      ...entry,
      name: saved.name ?? entry.name,
      category: saved.category ?? '',
      description: saved.description ?? '',
      siteId: saved.siteId ?? null,
      siteName: saved.siteName ?? '',
      // Le rattachement écrit par cette pop-in est EXCLUSIF (SiteResolver::
      // setNodeSite retire les autres) : la liste vaut donc exactement ce que
      // le serveur vient de renvoyer. La recopier n'est pas un confort — garder
      // l'ancienne ferait juger le cloisonnement sur un état périmé.
      siteIds: saved.siteId ? [Number(saved.siteId)] : [],
      signature: `${saved.name ?? entry.name}\u0000${saved.definition ?? ''}`,
    })))
    setMetaBlock(null)
    // Changer le site d'un bloc le fait sortir du catalogue ouvert : sans un mot,
    // l'éditeur voit sa ligne disparaître après un simple enregistrement.
    const stays = belongsToSite(
      { siteIds: saved.siteId ? [Number(saved.siteId)] : [], category: saved.category ?? '' },
      siteId,
    )
    notify({
      type: 'success',
      message: stays
        ? `Informations de « ${saved.name ?? blockDef.name} » enregistrées.`
        : `Informations enregistrées. « ${saved.name ?? blockDef.name} » est désormais rattaché à ${saved.siteName || 'la bibliothèque partagée'} : il n'est plus proposé sur cette page.`,
    })
  }, [setRegistry, siteId])

  // Supprimée côté Drupal — qui a vérifié qu'aucun contenu ne s'en sert : elle
  // disparaît du catalogue sans rechargement.
  const handleDeleted = useCallback((blockDef) => {
    setRegistry(prev => prev.filter(entry => entry.type !== blockDef.type))
    setDeleteBlock(null)
    notify({ type: 'success', message: `Bloc « ${blockDef.name} » supprimé.` })
  }, [setRegistry])

  // Échap ferme la sur-pop-in d'abord, le catalogue ensuite : chaque niveau
  // s'empile dans useEscapeClose, montée en dernier = servie en premier.
  useEscapeClose(onClose)

  const isEmpty = filtered.length === 0
  // Une sur-pop-in est ouverte : le catalogue sort du parcours clavier.
  const overlayOpen = Boolean(previewBlock || metaBlock || deleteBlock)

  // Portail vers <body> + z-index au-dessus de tout le chrome du builder : la
  // pop-in couvre 99 % du viewport, or le canevas contient du contenu qui
  // s'empile haut (une navbar de bloc en `z-[100]`, le panneau Assistant en
  // `z-[200]`) et qui traverserait la modale rendue en place, au z-50 du DS.
  return createPortal(
    <div
      id="builder-catalog-modal"
      className="modal-scrim modal-scrim-full"
      {...scrim}
    >
      <div
        className="modal-panel modal-panel-full"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={e => e.stopPropagation()}
        // Sur-pop-in ouverte → ce panneau sort du parcours clavier et de l'arbre
        // d'accessibilité. `aria-modal` seul ne suffit pas : sans `inert`, Tab
        // quitte l'aperçu et parcourt la recherche et les lignes de dessous.
        inert={overlayOpen ? true : undefined}
      >
        {/* En-tête */}
        <div className="modal-header">
          <div className="flex min-w-0 items-center gap-3">
            <h2 id={titleId} className="truncate">Ajouter un bloc</h2>
            {siteId ? (
              <span className="tag tag-iris" title="Seuls les blocs rattachés à ce site sont proposés">
                Site : {siteName || `#${siteId}`}
              </span>
            ) : (
              <span className="tag" title="Hors d'un site, seuls les blocs non rattachés sont proposés">
                Bibliothèque partagée
              </span>
            )}
            <span className="text-sm font-normal text-slate-500">
              {filtered.length} bloc{filtered.length > 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {/* Aucun bloc ne convient : plutôt que de fermer et d'aller chercher
                l'assistant, on lui passe la main d'ici — et la position cliquée
                est retenue pour que le bloc créé atterrisse au bon endroit. */}
            <button
              type="button"
              onClick={handleCreate}
              className="btn btn-primary btn-sm"
              title="Décrire le bloc voulu à l'assistant ; il le créera et le posera ici"
            >
              <Sparkles size={15} aria-hidden="true" />
              Créer un bloc
            </button>
            <button
              type="button"
              onClick={onClose}
              className="btn btn-ghost"
              aria-label="Fermer le catalogue"
            >
              <X size={18} aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Barre d'outils : recherche · catégories · affichage */}
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 px-5 py-3">
          <div className="relative min-w-64 flex-1">
            <Search
              size={15}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              aria-hidden="true"
            />
            <input
              type="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher un bloc…"
              autoFocus
              className="input pl-9"
              aria-label="Filtrer les blocs"
            />
          </div>

          <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filtrer par catégorie">
            {categories.map(cat => (
              <button
                key={cat}
                type="button"
                onClick={() => setActiveCategory(cat)}
                aria-pressed={activeCategory === cat}
                className={activeCategory === cat ? "tag tag-iris cursor-pointer" : "tag cursor-pointer hover:bg-slate-200"}
              >
                {cat === ALL ? ALL_LABEL : cat}
              </button>
            ))}
          </div>

          {/* Un axe à part des catégories — il s'y combine au lieu de les
              remplacer. Même compte que celui qui autorise « Supprimer ». Rien
              d'éligible → pas de filtre : le vide dirait « tout est posé » d'un
              site qui n'a simplement aucun bloc. */}
          {usageCounts && eligible.length > 0 && (
            <div className="flex items-center border-l border-slate-200 pl-3">
              <button
                type="button"
                onClick={() => setOnlyUnused(v => !v)}
                aria-pressed={onlyUnused}
                aria-describedby={unusedHintId}
                className={onlyUnused ? 'tag tag-iris cursor-pointer' : 'tag cursor-pointer hover:bg-slate-200'}
                title="Blocs posés sur aucune page ni aucun tableau de bord — ceux que l'on peut supprimer"
              >
                Non utilisés · {unusedCount}
              </button>
              {/* Le `title` ne se restitue ni au toucher ni sûrement en vocal. */}
              <span id={unusedHintId} className="sr-only">
                Blocs posés sur aucune page ni aucun tableau de bord : ce sont ceux que l'on peut supprimer.
              </span>
            </div>
          )}

          <div className="ml-auto flex items-center gap-3">
            {/* La largeur d'appareil ne pilote QUE les rendus de la galerie :
                en affichage « Liste », rien n'est monté, le sélecteur ne
                changerait rien à l'écran. On le masque plutôt que de proposer
                trois boutons sans effet — la sur-pop-in d'aperçu porte le sien,
                donc on peut toujours changer de largeur là où ça se voit. */}
            {view === 'render' && <DeviceToggle current={device} onChange={setDevice} />}
            <div className="flex items-center gap-1" role="group" aria-label="Mode d'affichage">
              <ViewToggle current={view} value="list" onChange={setView} icon={<List size={15} aria-hidden="true" />} label="Liste" />
              <ViewToggle current={view} value="render" onChange={setView} icon={<LayoutGrid size={15} aria-hidden="true" />} label="Rendu" />
            </div>
          </div>
        </div>

        {/* Corps */}
        <div className="modal-body flex-1">
          {isEmpty ? (
            <div className="empty-state">
              <span className="empty-state-icon"><LayoutGrid size={22} aria-hidden="true" /></span>
              <p className="font-semibold text-slate-700">
                {onlyUnused ? 'Aucun bloc non utilisé' : 'Aucun bloc disponible'}
              </p>
              <p className="max-w-md text-slate-500">
                {onlyUnused && !search.trim() && activeCategory === ALL
                  ? 'Chaque bloc proposé ici est posé sur au moins une page ou un tableau de bord.'
                  : search.trim() || activeCategory !== ALL
                  ? 'Aucun bloc ne correspond à cette recherche.'
                  : siteId
                    ? `Aucune définition de bloc n'est rattachée au site « ${siteName || `#${siteId}`} ». Rattachez-y un bloc pour pouvoir le poser sur cette page.`
                    : 'Aucun bloc de la bibliothèque partagée n\'est disponible : les blocs rattachés à un site ne se posent que sur les pages de ce site.'}
              </p>
            </div>
          ) : (
            Object.entries(grouped).map(([category, blocks]) => (
              <section key={category} className="mb-7 last:mb-0">
                {/* N'afficher le label de groupe que si aucune catégorie n'est filtrée */}
                {activeCategory === ALL && (
                  <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-400">
                    {category}
                  </h3>
                )}
                {view === 'list' ? (
                  <ul className="flex flex-col gap-1.5">
                    {blocks.map(block => (
                      <BlockRow
                        key={block.type}
                        block={block}
                        onSelect={handleSelect}
                        onPreview={setPreviewBlock}
                        onAiEdit={handleAiEdit}
                        onAiDerive={handleAiDerive}
                        onEditMeta={setMetaBlock}
                        onDelete={setDeleteBlock}
                      />
                    ))}
                  </ul>
                ) : (
                  // `auto-rows-fr` : toutes les lignes prennent la hauteur de la plus
                  // haute carte de la galerie, et chaque carte s'y étire (voir
                  // `BlockRenderCard`) — une grille de vignettes en escalier se lit
                  // mal, l'oeil compare des blocs, pas des hauteurs.
                  <div className="grid auto-rows-fr grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {blocks.map(block => (
                      <BlockRenderCard
                        key={block.type}
                        block={block}
                        // Rang GLOBAL, pas le rang dans la catégorie : c'est ce
                        // qui ordonne la file de montage sur toute la galerie.
                        index={renderOrder.get(block.type) ?? 0}
                        device={device}
                        onSelect={handleSelect}
                        onPreview={setPreviewBlock}
                        onAiEdit={handleAiEdit}
                        onAiDerive={handleAiDerive}
                        onEditMeta={setMetaBlock}
                        onDelete={setDeleteBlock}
                      />
                    ))}
                  </div>
                )}
              </section>
            ))
          )}
        </div>
      </div>

      {previewBlock && (
        <BlockPreviewModal
          block={previewBlock}
          device={device}
          onDeviceChange={setDevice}
          onClose={() => setPreviewBlock(null)}
          onSelect={handleSelect}
        />
      )}

      {metaBlock && (
        <BuilderDefMetaModal
          block={metaBlock}
          // Les catégories DÉJÀ en usage, tout le registry confondu — y compris
          // celles que le cloisonnement par site masque ici : ranger un bloc dans
          // une catégorie existante ne doit pas dépendre de la page ouverte.
          categories={knownCategories}
          onClose={() => setMetaBlock(null)}
          onSaved={saved => handleMetaSaved(metaBlock, saved)}
        />
      )}

      {deleteBlock && (
        <BuilderDefDeleteModal
          block={deleteBlock}
          onClose={() => setDeleteBlock(null)}
          onDeleted={handleDeleted}
        />
      )}
    </div>,
    document.body
  )
}

/**
 * Les trois appareils d'aperçu. Même vocabulaire que la barre du builder
 * (`BuilderHeader`) et que `hiddenOn` — mobile / tablette / desktop —, et les
 * mêmes bandes de largeur : ce que le catalogue montre est ce que le canevas
 * montrera. La largeur choisie DIMENSIONNE le conteneur de requêtes de
 * l'aperçu, elle ne fait pas que rogner le cadre : c'est elle qui décide des
 * `@container` du bloc.
 */
const DEVICES = [
  { id: 'mobile', Icon: Smartphone, label: 'Mobile' },
  { id: 'tablet', Icon: Tablet, label: 'Tablette' },
  { id: 'desktop', Icon: Monitor, label: 'Ordinateur' },
]

const DeviceToggle = ({ current, onChange }) => (
  <div className="flex items-center gap-1" role="group" aria-label="Largeur d'aperçu">
    {DEVICES.map(d => (
      <button
        key={d.id}
        type="button"
        onClick={() => onChange(d.id)}
        aria-pressed={current === d.id}
        aria-label={d.label}
        title={`Aperçu ${d.label.toLowerCase()} — ${DEVICE_PREVIEW_WIDTHS[d.id]} px`}
        className={current === d.id ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
      >
        <d.Icon size={15} aria-hidden="true" />
      </button>
    ))}
  </div>
)

const ViewToggle = ({ current, value, onChange, icon, label }) => (
  <button
    type="button"
    onClick={() => onChange(value)}
    aria-pressed={current === value}
    className={current === value ? "btn btn-primary btn-sm" : "btn btn-secondary btn-sm"}
  >
    {icon}
    {label}
  </button>
)

/**
 * Le menu d'actions d'un bloc du catalogue — tout ce qui vise la DÉFINITION,
 * et non l'instance qu'on est en train de poser :
 *
 *  - Modifier avec l'IA : réécrit la définition ⇒ toutes ses instances,
 *    partout, changent.
 *  - Créer à partir de : la définition sert de modèle et sort intacte ; le
 *    nouveau bloc est posé là où l'éditeur a cliqué « + ».
 *  - Modifier les informations : nom, description, catégorie, site — ce que le
 *    catalogue raconte du bloc, sans toucher à ce qu'il rend.
 *  - Supprimer : refusée par Drupal tant qu'un contenu s'en sert.
 *
 * Les deux dernières n'apparaissent que si Drupal a livré l'URL d'écriture ou de
 * suppression de la définition (`canEdit` / `canDelete`, cf.
 * `normalizeRegistryItem`) : les droits voyagent avec la donnée, l'écran n'en
 * décide aucun.
 *
 * Fermeture au clic extérieur et à Échap ; chaque entrée est un vrai bouton dans
 * un conteneur `role="menu"`, et le déclencheur porte `aria-expanded` /
 * `aria-haspopup`.
 */
// Gabarit du menu, en dur : il est positionné AVANT d'être mesuré (il n'existe
// pas encore au moment du calcul), et ses entrées ont toutes la même hauteur.
const ACTIONS_MENU_WIDTH = 272
const ACTIONS_MENU_ITEM_HEIGHT = 58

const BlockActionsMenu = ({ block, onAiEdit, onAiDerive, onEditMeta, onDelete, align = 'right' }) => {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState(null)
  const rootRef = useRef(null)
  const menuRef = useRef(null)
  const menuId = useId()

  const items = [
    {
      key: 'ai-edit',
      Icon: Sparkles,
      label: "Modifier ce bloc avec l'IA",
      hint: 'Toutes ses instances changeront',
      run: () => onAiEdit(block),
    },
    {
      key: 'ai-derive',
      Icon: Plus,
      label: 'Créer un bloc à partir de celui-ci',
      hint: "L'original reste intact",
      run: () => onAiDerive(block),
    },
    ...(block.canEdit ? [{
      key: 'meta',
      Icon: Pencil,
      label: 'Modifier les informations',
      hint: 'Nom, description, catégorie, site',
      run: () => onEditMeta(block),
    }] : []),
    ...(block.canDelete ? [{
      key: 'delete',
      Icon: Trash2,
      label: 'Supprimer ce bloc',
      hint: "Impossible tant qu'un contenu l'utilise",
      danger: true,
      run: () => onDelete(block),
    }] : []),
  ]

  // Position ancrée au déclencheur, recalculée au défilement et au
  // redimensionnement : rendu en portail, le menu ne suit plus sa carte tout
  // seul. Il bascule vers le haut quand la ligne est trop basse dans la
  // galerie, et reste dans le viewport horizontalement.
  useEffect(() => {
    if (!open) return
    const place = () => {
      const rect = rootRef.current?.getBoundingClientRect()
      if (!rect) return
      const wanted = align === 'right' ? rect.right - ACTIONS_MENU_WIDTH : rect.left
      const left = Math.min(Math.max(8, wanted), largeurUtile() - ACTIONS_MENU_WIDTH - 8)
      setPos(
        window.innerHeight - rect.bottom < items.length * ACTIONS_MENU_ITEM_HEIGHT + 12
          ? { left, bottom: window.innerHeight - rect.top + 4 }
          : { left, top: rect.bottom + 4 }
      )
    }
    place()
    // Capture : le défilement du corps de la modale ne bouillonne pas jusqu'à
    // `window`, or c'est LUI qui déplace le déclencheur.
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [open, align, items.length])

  useEffect(() => {
    if (!open) return
    // Le menu vit hors du sous-arbre du déclencheur (portail) : les deux
    // conteneurs comptent comme « dedans », sinon il se fermerait sur ses
    // propres clics.
    const onDocClick = (e) => {
      if (rootRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  // Échap referme le menu, pas le catalogue derrière — une touche, un niveau.
  useEscapeClose(() => setOpen(false), open)

  return (
    <div ref={rootRef} className="shrink-0">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="btn btn-secondary btn-sm"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`Actions pour ${block.name}`}
        title="Modifier ce bloc, en créer un autre à partir de lui, ou le supprimer"
      >
        <MoreVertical size={15} aria-hidden="true" />
      </button>
      {open && pos && createPortal(
        <div
          id={menuId}
          ref={menuRef}
          role="menu"
          aria-label={`Actions — ${block.name}`}
          className="popover-menu"
          style={{ ...pos, width: ACTIONS_MENU_WIDTH }}
        >
          {items.map((item, index) => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              onClick={() => { setOpen(false); item.run() }}
              className={`flex w-full items-start gap-2 px-3 py-2 text-left ${index > 0 ? 'border-t border-slate-100 ' : ''}${item.danger ? 'hover:bg-rose-50' : 'hover:bg-iris-50'}`}
            >
              <item.Icon
                size={14}
                className={`mt-0.5 shrink-0 ${item.danger ? 'text-rose-500' : 'text-slate-400'}`}
                aria-hidden="true"
              />
              <span>
                <span className={`block text-sm font-medium ${item.danger ? 'text-rose-700' : 'text-slate-800'}`}>{item.label}</span>
                <span className="block text-xs text-slate-500">{item.hint}</span>
              </span>
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  )
}

/**
 * Une ligne de l'affichage « Liste ».
 *
 * La ligne entière ajoute le bloc (la cible principale) ; les deux boutons de
 * droite sont explicites — « Visualiser » ouvre la sur-pop-in, « Ajouter »
 * double l'action de la ligne pour qui navigue au clavier ou cherche un bouton.
 * Un seul élément focusable par action, jamais un bouton dans un bouton.
 */
const BlockRow = ({ block, onSelect, onPreview, onAiEdit, onAiDerive, onEditMeta, onDelete }) => {
  const description = block.description ? stripFormatting(block.description) : ''
  return (
    <li className="group flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2 transition-colors hover:border-iris-300 hover:bg-iris-50">
      <button
        type="button"
        onClick={() => onSelect(block)}
        className="flex min-w-0 flex-1 items-center gap-3 text-left"
      >
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold text-slate-800">{block.name}</span>
          {description && (
            <span className="mt-0.5 line-clamp-1 text-xs text-slate-500" title={description}>
              {description}
            </span>
          )}
        </span>
      </button>
      {block.category && <span className="badge badge-neutral shrink-0">{block.category}</span>}
      <button
        type="button"
        onClick={() => onPreview(block)}
        className="btn btn-secondary btn-sm shrink-0"
        title="Voir le bloc tel qu'il sera rendu"
      >
        <Eye size={15} aria-hidden="true" />
        <span className="sr-only sm:not-sr-only">Visualiser</span>
      </button>
      <BlockActionsMenu
        block={block}
        onAiEdit={onAiEdit}
        onAiDerive={onAiDerive}
        onEditMeta={onEditMeta}
        onDelete={onDelete}
      />
      <button
        type="button"
        onClick={() => onSelect(block)}
        className="btn btn-primary btn-sm shrink-0"
      >
        <Plus size={15} aria-hidden="true" />
        <span className="sr-only sm:not-sr-only">Ajouter</span>
      </button>
    </li>
  )
}

/**
 * Une carte de l'affichage « Rendu » : le composant lui-même, monté avec ses
 * données par défaut et réduit à l'échelle. C'est le seul aperçu qui ne ment
 * jamais — il suit la déf sans qu'on ait à maintenir une vignette.
 */
const BlockRenderCard = ({ block, index, device, onSelect, onPreview, onAiEdit, onAiDerive, onEditMeta, onDelete }) => (
  <div className="card flex h-full flex-col overflow-hidden">
    {/* Ni classe d'arrondi ni de bordure : la recette `.block-preview-frame`
        est hors `@layer` et l'emporterait de toute façon sur les utilitaires —
        c'est elle qui porte le collage au bord de la carte.
        `grow` (et non `flex-1`) : la hauteur mesurée du cadre reste sa base —
        c'est elle qui dimensionne la ligne de grille — et seul le surplus de
        la carte la plus haute est absorbé ici, en fond gris sous l'aperçu.
        Avec `flex-1` (base 0) tous les cadres se vaudraient et l'aperçu le
        plus haut serait tronqué. */}
    <BuilderBlockPreviewFrame
      block={block}
      index={index}
      renderWidth={DEVICE_PREVIEW_WIDTHS[device]}
      className="grow"
    />
    <div className="flex shrink-0 items-center gap-2 px-3 py-2.5">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-slate-800" title={block.name}>{block.name}</p>
        {block.category && <p className="truncate text-xs text-slate-400">{block.category}</p>}
      </div>
      <button
        type="button"
        onClick={() => onPreview(block)}
        className="btn btn-secondary btn-sm shrink-0"
        aria-label={`Visualiser ${block.name}`}
        title="Voir le bloc en grand"
      >
        <Eye size={15} aria-hidden="true" />
      </button>
      <BlockActionsMenu
        block={block}
        onAiEdit={onAiEdit}
        onAiDerive={onAiDerive}
        onEditMeta={onEditMeta}
        onDelete={onDelete}
      />
      <button
        type="button"
        onClick={() => onSelect(block)}
        className="btn btn-primary btn-sm shrink-0"
        aria-label={`Ajouter ${block.name}`}
      >
        <Plus size={15} aria-hidden="true" />
      </button>
    </div>
  </div>
)

/**
 * Sur-pop-in : le bloc grandeur nature, plus sa documentation.
 *
 * Elle remplace l'ancien écran « détails » : la description Markdown y est
 * toujours, mais à côté du rendu réel — on décide d'un bloc en le voyant, pas
 * en lisant sa fiche.
 *
 * Elle se monte à l'INTÉRIEUR du scrim du catalogue (avec un z-index
 * supérieur), donc son propre scrim doit stopper la propagation du clic :
 * sinon un clic à côté de l'aperçu remonte au scrim du catalogue et ferme les
 * DEUX — la recherche en cours avec. Même règle qu'Échap, qui ne ferme qu'un
 * niveau à la fois.
 *
 * Le focus entre dans le dialogue à l'ouverture et retourne d'où il venait à la
 * fermeture ; le panneau du catalogue est rendu `inert` pendant ce temps (côté
 * appelant), sans quoi Tab s'échapperait vers la liste de dessous.
 */
const BlockPreviewModal = ({ block, device, onDeviceChange, onClose, onSelect }) => {
  const scrim = useScrimDismiss(onClose)
  useEscapeClose(onClose)
  const titleId = useId()
  const closeRef = useRef(null)

  useEffect(() => {
    const previous = document.activeElement
    closeRef.current?.focus()
    return () => { if (previous instanceof HTMLElement) previous.focus() }
  }, [])

  return (
    <div
      className="modal-scrim modal-scrim-full modal-scrim-over"
      {...scrim}
    >
      <div
        className="modal-panel modal-panel-full"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={e => e.stopPropagation()}
      >
        <div className="modal-header">
          <div className="flex min-w-0 items-center gap-3">
            <h2 id={titleId} className="truncate">{block.name}</h2>
            {block.category && <span className="badge badge-neutral">{block.category}</span>}
            <span className="truncate font-mono text-xs font-normal text-slate-400">{block.type}</span>
          </div>
          <div className="flex items-center gap-3">
            {/* Même sélecteur qu'au catalogue, et même état : on change de
                largeur ici, la galerie derrière est déjà d'accord. */}
            <DeviceToggle current={device} onChange={onDeviceChange} />
            <button
              type="button"
              ref={closeRef}
              onClick={onClose}
              className="btn btn-ghost"
              aria-label="Fermer l'aperçu"
            >
              <X size={18} aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Fond gris : un bloc court (bandeau d'alerte, barre de navigation)
            occupe quelques dizaines de pixels — sur un panneau blanc il flotte
            dans le vide, sur ce fond il se lit comme une bande posée sur une
            page. */}
        <div className="modal-body flex-1 space-y-4 bg-slate-50">
          {/* Rendu grandeur nature : pas de mise à l'échelle, on veut le bloc
              tel qu'il occupera la page. Inerte et retiré de l'arbre
              d'accessibilité — c'est un aperçu, pas une interface : d'où le
              texte de remplacement, sans quoi le dialogue serait muet en
              restitution vocale pour une déf sans description. */}
          <p className="sr-only">
            Aperçu visuel du bloc « {block.name} » en largeur {device === 'desktop' ? 'ordinateur' : device === 'tablet' ? 'tablette' : 'mobile'} ({DEVICE_PREVIEW_WIDTHS[device]} pixels), tel qu'il sera rendu avec son contenu par défaut.
          </p>
          {/* Le bloc est rendu à la largeur EXACTE de l'appareil, centré, sans
              mise à l'échelle : c'est un vrai rendu, pas une vignette. Le
              conteneur défile latéralement si la fenêtre est plus étroite que
              l'appareil choisi — sans quoi le bloc serait comprimé et l'aperçu
              mentirait sur ses requêtes de conteneur. */}
          <div className="overflow-x-auto">
            <div
              className="block-preview-live mx-auto overflow-hidden rounded-xl border border-slate-200 shadow-sm"
              style={{ width: DEVICE_PREVIEW_WIDTHS[device] }}
              inert
              aria-hidden="true"
            >
              <BuilderBlockPreview block={block} lazy={false} width={DEVICE_PREVIEW_WIDTHS[device]} />
            </div>
          </div>

          {block.description && (
            <div className="card p-4">
              <h3 className="mb-1.5 text-xs font-bold uppercase tracking-wide text-slate-400">
                Description
              </h3>
              <div className="prose prose-sm max-w-none break-words text-slate-600 prose-pre:bg-slate-50 prose-pre:text-xs prose-pre:text-slate-700 prose-code:text-iris-700 prose-code:before:content-none prose-code:after:content-none">
                <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                  {block.description}
                </ReactMarkdown>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Fermer
          </button>
          <button type="button" onClick={() => onSelect(block)} className="btn btn-primary">
            <Plus size={16} aria-hidden="true" />
            Ajouter ce bloc
          </button>
        </div>
      </div>
    </div>
  )
}
